import axios from 'axios';

/**
 * The PUT is capped below 100 % because it is not the last step.
 *
 * An asset does not exist until confirm returns — that is where the magic
 * number is checked and the dimensions are read — so a bar that hit 100 % on
 * the PUT would sit there looking finished while the file could still be
 * rejected. The last tenth belongs to confirm.
 */
export const PUT_PROGRESS_CEILING = 90;

export type PutFile = (
  url: string,
  file: File,
  contentType: string,
  options: { signal?: AbortSignal; onProgress: (percent: number) => void },
) => Promise<void>;

/**
 * PUT the bytes at a signed URL.
 *
 * Uses bare axios rather than `assetLibraryClient` on purpose: the client
 * attaches an Okta bearer token, and an extra `Authorization` header on a
 * signed-URL request makes GCS reject the signature. For the same reason the
 * Content-Type must be exactly the value the backend echoed back in
 * `UploadTarget` — the signature is computed over it.
 *
 * In a module of its own, apart from the batch runner, so a film's script can
 * be uploaded from the detail overlay without loading the runner.
 */
export const putFileToSignedUrl: PutFile = async (
  url,
  file,
  contentType,
  { signal, onProgress },
) => {
  await axios.put(url, file, {
    headers: { 'Content-Type': contentType },
    signal,
    // No auth, no withCredentials: this request is authorised by the signature
    // in the URL and nothing else.
    withCredentials: false,
    onUploadProgress: (event) => {
      if (!event.total) return;
      onProgress(Math.round((event.loaded / event.total) * PUT_PROGRESS_CEILING));
    },
  });
};
